import React, { useEffect, useRef, useState } from 'react'
import { Form } from '@unform/web'
import Input from 'components/elements/Inputs/Input'
import { FormHandles, SubmitHandler } from '@unform/core/typings/types'
import { trackPromise } from 'react-promise-tracker'
import { AdditionalSignUpAttributes, LoginSteps } from 'lib/tracking/attribution/signupattributes'
import AuthenticationService from 'services/AuthenticationService'
import { ApiError } from 'services/ApiService'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { toast } from 'react-toastify'
import NewButton from 'components/elements/NewButton'
import { useTranslation } from 'next-i18next'
import { handleFormValidationErrors } from 'lib/validation/form'
import AuthCaptchaDialog from './AuthCaptchaDialog'
import Stack from 'components/elements/Stack'
import { getUploadAlternativeEmail, storeAuthenticationOption } from 'lib/tracking/storage'
import { ButtonText } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import { getUsernameOutOfEmail } from 'lib/formatting/auth'
import styled from 'styled-components'
import { validateEmail } from 'lib/validation/email'

interface FormData {
    email: string
    password: string
    acceptTerms: boolean
}

interface Data {
    name: string
    email: string
    password: string
    acceptTerms: boolean
    twoFactorAuthenticationToken: string | null
    hCaptchaToken: string | null
}

const Container = styled.div<{ isHidden?: boolean }>`
    display: ${({ isHidden }) => (isHidden ? 'none' : 'grid')};
    grid-template-columns: 400px 150px;
    gap: 15px;
    position: relative;
    top: 2px;
    left: 10px;
    place-content: center;
    h5 {
        color: ${theme.colors.white};
    }
    input {
        height: 30px !important;
    }
    button {
        align-items: center;
        div {
            height: 40px !important;
            width: 100%;
        }
    }
`

export const loadingAreas = {
    container: 'authEmail',
}

enum CurrentSteps {
    Email = 'EMAIL',
    Password = 'PASSWORD',
}

interface Props {
    type: LoginSteps
    additionalSignUpAttributes: AdditionalSignUpAttributes | null
    onClick(): void
    onSuccess(isNewAccount: boolean): void
    disabled?: boolean
    removeMargins?: boolean
    isDark?: boolean
    isSmall?: boolean
    buttonText?: string
    onContinueButtonClick?: () => void
}

function AuthEmailStepByStep(props: Props) {
    const formRef = useRef<FormHandles>(null)
    const { t: tCommon } = useTranslation('common')
    const [showCaptchaDialog, setShowCaptchaDialog] = useState(false)
    const [data, setData] = useState<Data | null>(null)
    const [uploadAlternativeEmail, setUploadAlternativeEmail] = useState<string | null>(null)
    const [currentStep, setCurrentStep] = useState<CurrentSteps>(CurrentSteps.Email)

    useEffect(() => {
        setUploadAlternativeEmail(getUploadAlternativeEmail())
    }, [])

    const handleSubmit: SubmitHandler<FormData> = async (formData) => {
        props.onClick()

        const name = getUsernameOutOfEmail(formData.email)

        storeAuthenticationOption('Email')

        const data: Data = {
            ...formData,
            name,
            twoFactorAuthenticationToken: null,
            hCaptchaToken: null,
        }
        setData(data)
        handle(data)
    }

    const handleCaptchaVerify = (hCaptchaToken: string) => {
        if (!data) return
        setShowCaptchaDialog(false)
        const updatedData: Data = {
            ...data,
            hCaptchaToken,
        }

        setData(updatedData)
        handle(updatedData)
    }

    const handle = (data: Data) => {
        if (props.type === LoginSteps.SignIn) {
            signIn(data.email, data.password, data.twoFactorAuthenticationToken, data.hCaptchaToken)
        } else {
            signUp(data.name, data.email, data.password, data.hCaptchaToken, data.twoFactorAuthenticationToken)
        }
    }

    const signIn = (
        email: string,
        password: string,
        twoFactorAuthenticationToken: string | null,
        hCaptchaToken: string | null
    ) => {
        trackPromise(
            AuthenticationService.signInEmail(email, password, twoFactorAuthenticationToken, hCaptchaToken)
                .then(() => props.onSuccess(false))
                .catch((error) => {
                    if (error instanceof ApiError) {
                        if (error.type === ErrorTypes.InvalidCredentials || error.type === ErrorTypes.FormValidation) {
                            toast.error(tCommon('common/formValidationInvalidCredentialsError'))
                        } else if (error.type === ErrorTypes.Invalid2FAToken) {
                            toast.error(tCommon('common/formValidationInvalid2FAError'))
                        } else if (error.type === ErrorTypes.TooManyLoginAttempts) {
                            toast.error(tCommon('common/apiErrorHandlerTooManyLoginAttemptsError'))
                            return
                        } else if (error.type === ErrorTypes.Requires2FA) {
                            handle2FA()
                        } else if (error.type === ErrorTypes.RequiresCaptcha) {
                            setShowCaptchaDialog(true)
                        } else {
                            error.handleUnknownError(tCommon, 'auth.SignInEmail')
                        }
                    } else {
                        throw error
                    }
                }),
            loadingAreas.container
        )
    }

    const handle2FA = () => {
        const token = window.prompt(tCommon('common/handle2FA'))
        if (!token) {
            return
        }

        if (!data) return
        const updatedData: Data = {
            ...data,
            twoFactorAuthenticationToken: token,
        }
        setData(updatedData)
        handle(updatedData)
    }

    const signUp = async (
        name: string,
        email: string,
        password: string,
        hCaptchaToken: string | null,
        twoFactorAuthenticationToken: string | null
    ) => {
        if (!props.additionalSignUpAttributes) return

        trackPromise(
            AuthenticationService.signUpEmail(name, email, password, props.additionalSignUpAttributes, hCaptchaToken)
                .then(() => props.onSuccess(true))
                .catch((error) => {
                    if (error instanceof ApiError) {
                        if (error.type === ErrorTypes.FormValidation) {
                            handleFormValidationErrors(tCommon, error, formRef.current!)
                        } else if (error.type === ErrorTypes.EmailInUse) {
                            return signIn(email, password, twoFactorAuthenticationToken, hCaptchaToken)
                        } else if (error.type === ErrorTypes.RequiresCaptcha) {
                            setShowCaptchaDialog(true)
                        } else {
                            error.handleUnknownError(tCommon, 'auth.signUpEmail')
                            return
                        }
                    } else {
                        throw error
                    }
                }),
            loadingAreas.container
        )
    }

    const validateAndUpdateEmail = () => {
        const email = formRef.current?.getFieldValue('email')

        if (!email || !validateEmail(email)) {
            toast.warning(tCommon('common/formValidationEmailError'))
            return
        }

        props.onContinueButtonClick && props.onContinueButtonClick()
        setCurrentStep(CurrentSteps.Password)
    }

    useEffect(() => {
        const trackKeyEvent = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault()

                if (currentStep === CurrentSteps.Email) {
                    validateAndUpdateEmail()
                } else {
                    formRef.current?.submitForm()
                }
            }
        }

        document.addEventListener('keydown', (event) => trackKeyEvent(event))
        return () => {
            document.removeEventListener('keydown', (event) => trackKeyEvent(event))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep])

    return (
        <div>
            <AuthCaptchaDialog show={showCaptchaDialog} onVerify={handleCaptchaVerify} />
            <Form ref={formRef} onSubmit={handleSubmit} initialData={{ email: uploadAlternativeEmail }}>
                <Stack gutter={15}>
                    <Container isHidden={currentStep !== CurrentSteps.Email} className="email-container">
                        <Input
                            hideLabel
                            name="email"
                            type="email"
                            id="email"
                            label={tCommon('common/authEmailFormEmailLabel')}
                            placeholder={tCommon('common/authEmailFormEmailLabel')}
                            autoComplete="email"
                            autoFocus={!props.removeMargins}
                            noMargin={!!props.removeMargins}
                            height={props.isSmall ? 40 : undefined}
                        />
                        <NewButton
                            fullWidth
                            color={props.isDark ? theme.colors.white : theme.colors.deepSeaBlue}
                            borderRadius={theme.borderRadius.small}
                            height={40}
                            width={300}
                            buttonType="button"
                            className="email-continue-click"
                            onClick={validateAndUpdateEmail}
                            disabled={!!props.disabled}
                        >
                            <ButtonText
                                fontFamily="var(--font-open-sans)"
                                fontWeight={props.isSmall ? 400 : 600}
                                color={props.isDark ? `${theme.colors.body1Black}` : theme.colors.white}
                            >
                                {tCommon('common/continue')}
                            </ButtonText>
                        </NewButton>
                    </Container>
                    <Container isHidden={currentStep !== CurrentSteps.Password} className="password-container">
                        <Input
                            hideLabel
                            name="password"
                            type="password"
                            label={tCommon('common/authEmailFormPasswordLabel')}
                            placeholder={tCommon('common/authEmailFormPasswordLabel')}
                            autoComplete={props.type === LoginSteps.SignIn ? 'current-password' : 'new-password'}
                            noMargin={!!props.removeMargins}
                            height={props.isSmall ? 40 : undefined}
                        />
                        <NewButton
                            fullWidth
                            buttonType="submit"
                            color={props.isDark ? theme.colors.white : theme.colors.deepSeaBlue}
                            borderRadius={theme.borderRadius.small}
                            height={40}
                            width={300}
                            disabled={!!props.disabled}
                            loadingArea={loadingAreas.container}
                        >
                            <ButtonText
                                fontFamily="var(--font-open-sans)"
                                fontWeight={props.isSmall ? 400 : 600}
                                color={props.isDark ? `${theme.colors.body1Black}` : theme.colors.white}
                            >
                                {tCommon(
                                    props.type === LoginSteps.SignIn ? 'common/navBarLoginLink' : 'common/navBarSignUpLink'
                                )}
                            </ButtonText>
                        </NewButton>
                    </Container>
                </Stack>
            </Form>
        </div>
    )
}

export default AuthEmailStepByStep
